import _ from "underscore";
import DriveView from "../../DriveView";
import DutyStatusLog from "../../model/DutyStatusLog";
import { dstCalculator } from "../../utils/date";
import dayjs from "../../utils/dayjs";
import { Dialog } from "./dialog";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import AvailabilityDisplay from "./availabilityDisplay/availabilityDisplay";
import HosProcess from "../../services/hosProcess";
import { SyncType } from "../../sync/sync.types";
import AppView from "../../app.view";
import { hashToArray } from "../../utils/vanilla";

import dialogHTML from "./zoneCycleChangeDialog.tmpl";

interface IChangeLogDictionary {
    [ dateString: string ]: {
        formattedDate: string;
        displayString: string;
    };
}

interface IZoneChangeDialogOptions {
    app: AppView;
    logsToVerify: DutyStatusLog[];
}

export default class ZoneCycleChangeDialog extends DriveView<Backbone.Model> {

    protected strings = [
        "{0} days",
        "Cycle 1",
        "Cycle 2",
        "United States",
        "Canada South of 60",
        "Canada North of 60",
        "{0} (start of day: {1})",
        "h",
        "{minutes}m",
        "{0} Break duration",
        "{0} Break",
        "{0} Break required in"
    ];
    private readonly availabilityDisplay: AvailabilityDisplay;
    private readonly hosUtils: HosProcess;
    private readonly template = _.template(dialogHTML);
    private readonly logsToVerify: DutyStatusLog[] = [];

    constructor(options: IZoneChangeDialogOptions) {
        super(options);
        this.availabilityDisplay = new AvailabilityDisplay();
        this.hosUtils = this.app.hosUtils;
        this.logsToVerify = options.logsToVerify;
    }

    open(): Promise<void | Dialog> {
        return this.translate()
            .then(() => {
                let currentUser = this.app.userService.getCurrentUser();
                if (!currentUser) {
                    return Promise.resolve();
                }

                let changeLogList = this.getChangeLogHtml();
                if (!changeLogList.length) {
                    return Promise.resolve();
                }

                let availabilityDisplayHtml = "";
                let ruleSetId = this.hosUtils.getLatestHosRuleSetIdShortened(true);
                let latestLog = this.hosUtils.getLatestLogForUser(currentUser as IIdEntity);
                let hasAvailability = this.hosUtils.hasAvailability(ruleSetId);
                let ruleResult = hasAvailability ? this.hosUtils.getRuleResult(true) : undefined;

                let violationErrors = hasAvailability ? this.hosUtils.checkIfInOrNearViolation(ruleResult, ruleSetId) : [];
                return (violationErrors.length ? Promise.all(violationErrors.map(violationError => violationError.getNoticeBoxMessage(this.translator))) : Promise.resolve([]))
                    .then(violationMessages => {
                        let violationWarnings = "";
                        violationMessages.forEach(violationMessage => {
                            violationWarnings += violationMessage;
                        });

                        if (hasAvailability && ruleResult) {
                            availabilityDisplayHtml = this.availabilityDisplay.getDisplay(
                                ruleResult,
                                ruleSetId,
                                latestLog,
                                this.getString("{0} days"),
                                "HOS",
                                undefined,
                                false,
                                this.getString("h"),
                                this.getString("{minutes}m").replace("{minutes}", ""),
                                this.getString("{0} Break duration"),
                                this.getString("{0} Break"),
                                this.getString("{0} Break required in")
                            );
                        }

                        let dialogTemplate = this.template({
                            changeLogList,
                            availabilityDisplayHtml,
                            violationWarnings
                        });

                        let dialog = new Dialog(
                            "new-zoneCycle-changes-dialog",
                            dialogTemplate,
                            this.translator,
                            "Rulesets modified",
                            [{
                                text: "Confirm",
                                id: "verify-zone-cycle-logs",
                                callback: () => {
                                    return this.verifyLogs().then(() => dialog.remove());
                                },
                                spinner: true
                            }],
                            {
                                isNightMode: this.app.store.getState().data.nightMode
                            }
                        );
                        return dialog.open();
                    });
            });
    }

    private getChangeLogHtml() {
        let logDictByDate: IChangeLogDictionary = {};
        this.logsToVerify.forEach(log => {
            if (!logDictByDate.hasOwnProperty(log.dateTime)) {
                logDictByDate[log.dateTime] = {
                    formattedDate: dayjs(dstCalculator.toLocalDateTime(log.getDate())).format("lll"),
                    displayString: ""
                };
            }

            let logFormattedStatus = this.getDisplayString(log);
            logDictByDate[log.dateTime].displayString += logFormattedStatus;
        });

        return hashToArray(logDictByDate);
    }

    private getDisplayString(log: DutyStatusLog) {
        let stringTmpl = `<span><span class="noticeBox--heading">${ log.isCycleChangeLog() ? "Cycle" : "Zone" }:</span><span class="noTranslate"> {0}</span></span>`;
        let status = log.status;
        switch (status) {
            case "CanadaCycleOne":
                return stringTmpl.replace("{0}", this.getString("Cycle 1"));
            case "CanadaCycleTwo":
                return stringTmpl.replace("{0}", this.getString("Cycle 2"));
            case "OperatingZoneAmerica":
                return stringTmpl.replace("{0}", this.getString("United States"));
            case "OperatingZoneCanadaSouthOf60":
                return stringTmpl.replace("{0}", this.getString("Canada South of 60"));
            case "OperatingZoneCanadaNorthOf60":
                return stringTmpl.replace("{0}", this.getString("Canada North of 60"));
            default:
                return "";
        }
    }

    private verifyLogs() {
        return Promise.all(this.logsToVerify.map(log => {
            log.verify();
            return this.app.sync.dutyStatusLogs.setItem(log, true);
        })).then(() => this.app.sync.synchronize(false, undefined, [SyncType.HOS]));
    }
}